import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdRemoveRedEye, MdEdit } from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import toast from 'react-hot-toast';
import Skeleton from '../../../../components/Skeleton';
import TableTemplate from '../../../../components/table/index';
import CustomModal from '../../../../components/modal/index';
import { getAllTherapyMaterial, deleteTherapyMaterial } from '../../../../controllers/TherapyMaterialController';

const TableComponent = ({ t }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [selectedID, setSelectedID] = useState(null);
    const navigate = useNavigate();
    
    const columns = useMemo(() => [
        { field: 'title', title: t('common.title.title'), sortable: true },
        { field: 'type', title: t('common.type.title'), sortable: true },
        { field: 'created_by', title: t('common.created.by'), sortable: true },
        { field: 'created_at', title: t('common.created.title'), sortable: true },
        { field: 'updated_by', title: t('common.updated.by'), sortable: true },
        { field: 'updated_at', title: t('common.updated.title'), sortable: true }
    ], [t]);
    
    const actions = useMemo(() => [
        (row) => (
            <button
                className='focus:outline-none text-xl md:text-2xl text-successMessage hover:text-successText focus:text-successText'
                title={t('common.action.detail')}
                onClick={() => navigate(`/therapy-material/${row.id}/detail`)}
            >
                <MdRemoveRedEye />
            </button>
        ),
        (row) => (
            <button
                className='focus:outline-none text-xl md:text-2xl text-secondary hover:text-primary focus:text-primary'
                title={t('common.action.edit')}
                onClick={() => navigate(`/therapy-material/${row.id}/edit`)}
            >
                <MdEdit />
            </button>
        ),
        (row) => (
            <button
                className='focus:outline-none text-xl md:text-2xl text-errorMessage hover:text-errorText focus:text-errorText'
                title={t('common.action.delete')}
                onClick={() => handleOpenModal(row.id)}
            >
                <IoMdTrash />
            </button>
        ),
    ], [ t, navigate ]);
    
    const filters = useMemo(() => [
        { field: 'type', label: t('common.type.title'), by: t('common.type.article'), labelBy: t('common.type.article'), mode: 'contains' },
        { field: 'type', label: t('common.type.title'), by: t('common.type.video'), labelBy: t('common.type.video'), mode: 'contains' },
    ], [t]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                const result = await getAllTherapyMaterial(t('languages.select'));
                const formattedRows = result.therapy_materials.map((therapyMaterial) => {
                    const formattedType = therapyMaterial.type.toLowerCase();
                    
                    return {
                        id: therapyMaterial.id,
                        title: therapyMaterial.title,
                        type: t(`common.type.${formattedType}`),
                        created_by: therapyMaterial.created_by,
                        created_at: therapyMaterial.created_at,
                        updated_by: therapyMaterial.updated_by,
                        updated_at: therapyMaterial.updated_at
                    };
                });
                
                setRows(formattedRows);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            };
        };
        
        fetchData();
    }, [t]);
    
    const handleAdd = () => {
        navigate('/therapy-material/create');
    };
    
    const handleOpenModal = (id) => {
        setSelectedID(id);
        setOpen(true);
    };
    
    const handleClose = () => {
        setOpen(false);
    };
    
    const handleDelete = async () => {
        try {
            await deleteTherapyMaterial(selectedID);
            setRows(rows.filter((row) => row.id !== selectedID));
            setOpen(false);
            toast.success(t('therapyMaterial.delete.message'));
        } catch (error) {
            setOpen(false);
            toast.error(error.message);
        };
    };
    
    if (loading) {
        return (
        <Skeleton className='h-96 w-full rounded-lg' />
        );
    };
    
    return (
        <div>
        <TableTemplate
            rows={rows}
            columns={columns}
            actions={actions}
            filters={filters}
            onAddNew={handleAdd}
            fileName={t('menu.therapyMaterial')}
        />
        
        <CustomModal
            open={open}
            onClose={handleClose}
            onConfirm={handleDelete}
            title={t('therapyMaterial.delete.title')}
            description={t('therapyMaterial.delete.description')}
            confirmText={t('common.action.delete')}
            cancelText={t('common.action.cancel')}
        />
        </div>
    );
};

export default TableComponent;
